import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Thermometer, SprayCan, ChefHat, ClipboardCheck, Clock, ListChecks,
  ShieldCheck, LogOut, Delete, X, Loader2, AlertTriangle, ArrowLeft, Tablet,
} from "lucide-react";
import { API } from "@/lib/api";
import api from "@/lib/api";
import { tabletExtract } from "@/lib/tabletScan";
import { TabletSetup } from "@/components/tablet/TabletSetup";
import { ManagePins } from "@/components/tablet/ManagePins";
import { StaffPinScreen } from "@/components/tablet/StaffPinScreen";
import { TabletTasks } from "@/components/tablet/TabletTasks";
import { isTabletLockHeld, expireTabletLock, resetTabletHold } from "@/lib/tabletHold";

const DEVICE_KEY = "cc_tablet_device";

// Device-token client (shared, no personal auth). Separate from the signed-in manager session.
function deviceClient() {
  const token = localStorage.getItem(DEVICE_KEY) || "";
  return axios.create({ baseURL: API, headers: { "X-Tablet-Device": token } });
}
// Actor client: a short-lived, person-attributed session minted by a verified PIN. A 401 means the
// session expired or the device was revoked: the screen locks back to the staff PIN screen.
function actorClient(actorToken) {
  const c = axios.create({ baseURL: API, headers: { Authorization: `Bearer ${actorToken}`, "X-Tablet-Device": localStorage.getItem(DEVICE_KEY) || "" } });
  c.interceptors.response.use((r) => r, (e) => {
    if (e?.response?.status === 401) window.dispatchEvent(new Event("cc-tablet-session-ended"));
    return Promise.reject(e);
  });
  return c;
}
const IDLE_MS = 60000;

const TILE_META = {
  temperatures: { label: "Temperatures", icon: Thermometer, color: "text-sky-300" },
  cleaning: { label: "Cleaning", icon: SprayCan, color: "text-emerald-300" },
  prep: { label: "Prep", icon: ChefHat, color: "text-amber-300" },
  checklists: { label: "Checklists", icon: ClipboardCheck, color: "text-violet-300" },
};

function PinPad({ onSubmit, onCancel, person, busy }) {
  const [pin, setPin] = useState("");
  const press = (d) => setPin((p) => (p.length < 4 ? p + d : p));
  const back = () => setPin((p) => p.slice(0, -1));
  useEffect(() => { if (pin.length === 4) { onSubmit(pin); setPin(""); } }, [pin]); // eslint-disable-line
  return (
    <div className="fixed inset-0 z-50 bg-slate-950/90 flex items-center justify-center p-6" data-testid="tablet-pinpad">
      <div className="w-full max-w-sm bg-slate-900 rounded-3xl p-7 border border-slate-700">
        <div className="flex items-center justify-between mb-1">
          <p className="text-slate-400 text-sm">Who is recording this?</p>
          <button onClick={onCancel} className="text-slate-500 hover:text-slate-200" data-testid="tablet-pin-cancel"><X size={22} /></button>
        </div>
        <h3 className="text-2xl font-bold text-white mb-5">{person?.display_name}</h3>
        <div className="flex justify-center gap-3 mb-6">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className={`w-4 h-4 rounded-full ${i < pin.length ? "bg-brand" : "bg-slate-700"}`} />
          ))}
        </div>
        <div className="grid grid-cols-3 gap-3">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
            <button key={n} disabled={busy} onClick={() => press(String(n))} data-testid={`pin-key-${n}`}
              className="h-16 rounded-2xl bg-slate-800 text-white text-2xl font-semibold active:bg-brand/30 disabled:opacity-50">{n}</button>
          ))}
          <div />
          <button disabled={busy} onClick={() => press("0")} data-testid="pin-key-0"
            className="h-16 rounded-2xl bg-slate-800 text-white text-2xl font-semibold active:bg-brand/30 disabled:opacity-50">0</button>
          <button disabled={busy} onClick={back} data-testid="pin-key-back"
            className="h-16 rounded-2xl bg-slate-800 text-slate-300 flex items-center justify-center active:bg-slate-700"><Delete size={24} /></button>
        </div>
        {busy && <div className="flex justify-center mt-5 text-brand"><Loader2 className="animate-spin" /></div>}
      </div>
    </div>
  );
}

function RosterPicker({ roster, onPick, onCancel, title }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-950/90 flex items-center justify-center p-6" data-testid="tablet-roster">
      <div className="w-full max-w-lg bg-slate-900 rounded-3xl p-7 border border-slate-700">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-xl font-bold text-white">{title || "Who is recording this?"}</h3>
          <button onClick={onCancel} className="text-slate-500 hover:text-slate-200" data-testid="tablet-roster-cancel"><X size={22} /></button>
        </div>
        {roster.length === 0 && <p className="text-slate-400 text-sm">No staff have a kitchen PIN yet. A manager can set PINs in Manage PINs.</p>}
        <div className="grid grid-cols-2 gap-3 max-h-[55vh] overflow-auto">
          {roster.map((p) => (
            <button key={p.person_id} onClick={() => onPick(p)} data-testid={`roster-${p.person_id}`}
              className="p-4 rounded-2xl bg-slate-800 text-left hover:bg-slate-700 active:bg-brand/30">
              <p className="text-white font-semibold">{p.display_name}</p>
              <p className="text-slate-400 text-xs">{p.job_title || "Staff"}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function NumPad({ onValue }) {
  const [v, setV] = useState("");
  const press = (d) => setV((p) => (p.length < 6 ? p + d : p));
  return (
    <div>
      <div className="h-14 rounded-2xl bg-slate-800 text-white text-2xl font-semibold flex items-center justify-center mb-4" data-testid="numpad-display">{v || "0"}</div>
      <div className="grid grid-cols-3 gap-3">
        {["1","2","3","4","5","6","7","8","9",".","0","-"].map((n) => (
          <button key={n} onClick={() => press(n)} data-testid={`num-${n === "." ? "dot" : n === "-" ? "neg" : n}`} className="h-14 rounded-2xl bg-slate-800 text-white text-xl font-semibold active:bg-brand/30">{n}</button>
        ))}
      </div>
      <div className="flex gap-3 mt-4">
        <button onClick={() => setV((p) => p.slice(0, -1))} className="flex-1 h-12 rounded-2xl bg-slate-800 text-slate-300 flex items-center justify-center"><Delete size={20} /></button>
        <button onClick={() => { if (v !== "" && v !== "-") onValue(v); }} data-testid="numpad-confirm" className="flex-1 h-12 rounded-2xl bg-brand text-white font-semibold">Enter</button>
      </div>
    </div>
  );
}

function DeliveryCapture({ suppliers, onCancel, onCommit, onScan }) {
  const [supplierId, setSupplierId] = useState("");
  const [acceptance, setAcceptance] = useState("accepted");
  const [notes, setNotes] = useState("");
  const [corrective, setCorrective] = useState("");
  const [temp, setTemp] = useState(""); const [tmin, setTmin] = useState(""); const [tmax, setTmax] = useState(""); const [tcomment, setTcomment] = useState("");
  const [checks, setChecks] = useState({ packaging_ok: null, labels_dates_ok: null });
  const [file, setFile] = useState(null);
  const [scannedId, setScannedId] = useState(null);
  const [scanning, setScanning] = useState(false);
  const applyPrefill = (r) => {
    const p = { ...(r?.proposal || {}), ...r };
    const doc = p.proposed_document_number || p.document_number || p.delivery_number || p.invoice_number;
    const sup = p.proposed_supplier_name || p.supplier_name;
    if (doc) setNotes((n) => n || `Doc ${doc}`);
    if (sup) setNotes((n) => (n ? n + " · " : "") + `Supplier (scan): ${sup}`);
  };
  const setCheck = (k, v) => setChecks((c) => ({ ...c, [k]: c[k] === v ? null : v }));
  const build = () => ({
    supplier_id: supplierId || null, acceptance, notes, corrective, checks,
    temps: temp !== "" ? [{ label: "Delivery item", temperature: Number(temp), minimum: tmin !== "" ? Number(tmin) : null, maximum: tmax !== "" ? Number(tmax) : null, comments: tcomment }] : [],
  });
  return (
    <div className="fixed inset-0 z-40 bg-slate-950/90 flex items-center justify-center p-4 overflow-auto" data-testid="delivery-capture">
      <div className="w-full max-w-2xl bg-slate-900 rounded-3xl p-6 border border-slate-700 my-6">
        <div className="flex items-center justify-between mb-4"><h3 className="text-xl font-bold text-white">Record a delivery</h3><button onClick={onCancel} data-testid="delivery-cancel"><X className="text-slate-400" size={22} /></button></div>

        <p className="text-sm text-slate-400 mb-2">Supplier</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-5 max-h-40 overflow-auto">
          {suppliers.map((s, i) => (
            <button key={s.supplier_id} onClick={() => setSupplierId(s.supplier_id)} data-testid={`delivery-supplier-opt-${i}`} className={`p-3 rounded-xl text-left text-sm ${supplierId === s.supplier_id ? "bg-brand/30 border border-brand" : "bg-slate-800"}`}>{s.name}</button>
          ))}
          {suppliers.length === 0 && <p className="text-slate-500 text-sm col-span-full">No suppliers set up for this site.</p>}
        </div>

        <div className="flex gap-3 mb-5">
          <button onClick={() => setAcceptance("accepted")} data-testid="delivery-accept" className={`flex-1 py-3 rounded-xl font-semibold ${acceptance === "accepted" ? "bg-emerald-500/30 border border-emerald-500" : "bg-slate-800"}`}>Accept</button>
          <button onClick={() => setAcceptance("rejected")} data-testid="delivery-reject" className={`flex-1 py-3 rounded-xl font-semibold ${acceptance === "rejected" ? "bg-red-500/30 border border-red-500" : "bg-slate-800"}`}>Reject</button>
        </div>

        <p className="text-sm text-slate-400 mb-2">Temperature (optional)</p>
        <div className="grid grid-cols-3 gap-2 mb-2">
          <input value={temp} onChange={(e) => setTemp(e.target.value.replace(/[^\d.-]/g, ""))} inputMode="decimal" placeholder="°C" data-testid="delivery-temp" className="px-3 py-2 rounded-lg bg-slate-800 text-white" />
          <input value={tmin} onChange={(e) => setTmin(e.target.value.replace(/[^\d.-]/g, ""))} inputMode="decimal" placeholder="min" className="px-3 py-2 rounded-lg bg-slate-800 text-white" />
          <input value={tmax} onChange={(e) => setTmax(e.target.value.replace(/[^\d.-]/g, ""))} inputMode="decimal" placeholder="max" className="px-3 py-2 rounded-lg bg-slate-800 text-white" />
        </div>
        <input value={tcomment} onChange={(e) => setTcomment(e.target.value)} placeholder="Corrective comment if out of range" className="w-full px-3 py-2 rounded-lg bg-slate-800 text-white mb-5 text-sm" />

        <div className="flex gap-3 mb-5">
          {[["packaging_ok", "Packaging"], ["labels_dates_ok", "Labels/dates"]].map(([k, lbl]) => (
            <div key={k} className="flex-1">
              <p className="text-xs text-slate-400 mb-1">{lbl}</p>
              <div className="flex gap-2">
                <button onClick={() => setCheck(k, true)} data-testid={`delivery-check-${k}-ok`} className={`flex-1 py-2 rounded-lg text-sm ${checks[k] === true ? "bg-emerald-500/30" : "bg-slate-800"}`}>OK</button>
                <button onClick={() => setCheck(k, false)} data-testid={`delivery-check-${k}-bad`} className={`flex-1 py-2 rounded-lg text-sm ${checks[k] === false ? "bg-red-500/30" : "bg-slate-800"}`}>Issue</button>
              </div>
            </div>
          ))}
        </div>

        <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes" data-testid="delivery-notes" className="w-full px-3 py-2 rounded-lg bg-slate-800 text-white mb-3 text-sm" />
        {acceptance === "rejected" && (
          <input value={corrective} onChange={(e) => setCorrective(e.target.value)} placeholder="Reason / corrective action (required)" data-testid="delivery-corrective" className="w-full px-3 py-2 rounded-lg bg-slate-800 text-white mb-3 text-sm" />
        )}

        <div className="flex items-center gap-3 mb-5">
          <label className="px-3 py-2 rounded-lg bg-slate-800 text-sm text-slate-200 cursor-pointer" data-testid="delivery-photo-label">
            {file ? "Photo added ✓" : "📷 Invoice / photo"}
            <input type="file" accept="image/*" capture="environment" className="hidden" data-testid="delivery-photo" onChange={(e) => { setFile(e.target.files?.[0] || null); setScannedId(null); }} />
          </label>
          {file && <button disabled={scanning} onClick={() => onScan(file, applyPrefill, setScannedId, scannedId, setScanning)} data-testid="delivery-scan" className="px-3 py-2 rounded-lg bg-violet-500/25 text-violet-200 text-sm disabled:opacity-60">{scanning ? <span className="flex items-center gap-1"><Loader2 size={14} className="animate-spin" />Reading note… up to 3 min</span> : "Scan with AI (identify first)"}</button>}
        </div>

        <div className="flex gap-3">
          <button onClick={onCancel} className="flex-1 py-3 rounded-xl bg-slate-800">Cancel</button>
          <button disabled={!supplierId} onClick={() => onCommit(build(), file, scannedId)} data-testid="delivery-submit" className="flex-1 py-3 rounded-xl bg-brand text-white font-semibold disabled:opacity-50">Who is recording this? →</button>
        </div>
      </div>
    </div>
  );
}

export default function KitchenTablet() {
  const navigate = useNavigate();
  const [registered, setRegistered] = useState(!!localStorage.getItem(DEVICE_KEY));
  const [notice, setNotice] = useState(null);
  const [staff, setStaff] = useState(null); // in memory only: refresh / reopen never restores a person
  const staffRef = useRef(null);
  staffRef.current = staff;
  const [status, setStatus] = useState(null);
  const [roster, setRoster] = useState([]);
  const [view, setView] = useState("home"); // home | mytasks | manager | pins
  const [flow, setFlow] = useState(null); // {stage:'roster'|'pin', action}
  const [busy, setBusy] = useState(false);
  const [sel, setSel] = useState(null);
  const [taskFilter, setTaskFilter] = useState("all");
  const lockTimer = useRef(null);
  const staffClient = useMemo(() => (staff ? actorClient(staff.token) : null), [staff]);

  const loadStatus = useCallback(async () => {
    try {
      const d = deviceClient();
      const [s, r] = await Promise.all([d.get("/tablet/status"), d.get("/tablet/roster")]);
      setStatus(s.data); setRoster(r.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem(DEVICE_KEY); setStaff(null); setRegistered(false);
        setNotice("This tablet's registration was removed or is no longer valid. A manager needs to set it up again.");
      }
    }
  }, []);

  useEffect(() => { if (registered) loadStatus(); }, [registered, loadStatus]);
  useEffect(() => { if (!registered) return; const t = setInterval(loadStatus, 30000); return () => clearInterval(t); }, [registered, loadStatus]);

  // ---- staff session: Finish / inactivity lock / expiry ----
  const lock = useCallback((msg) => {
    const s = staffRef.current;
    if (s) deviceClient().post("/tablet/session/end", {}, { headers: { Authorization: `Bearer ${s.token}` } }).catch(() => {});
    clearTimeout(lockTimer.current);
    resetTabletHold();
    setStaff(null); setView("home"); setSel(null); setFlow(null); setBoard(null); setDelivery(null); setNumpad(null);
    loadStatus(); // roster/access changes show immediately for the next person
    if (msg) toast(msg);
  }, [loadStatus]);
  useEffect(() => {
    const h = () => { if (staffRef.current) lock("Session ended. Please sign in with your PIN again."); };
    window.addEventListener("cc-tablet-session-ended", h);
    return () => window.removeEventListener("cc-tablet-session-ended", h);
  }, [lock]);
  useEffect(() => {
    if (!staff) return undefined;
    let lastRefresh = Date.now();
    const fire = () => {
      if (isTabletLockHeld()) { lockTimer.current = setTimeout(fire, 5000); return; } // camera / photo picker open
      lock("Locked after 60 seconds without activity");
    };
    const arm = () => {
      if (expireTabletLock()) return;
      clearTimeout(lockTimer.current);
      lockTimer.current = setTimeout(fire, IDLE_MS);
      if (Date.now() - lastRefresh > 60000) { // keep the server session alive only while being used
        lastRefresh = Date.now();
        deviceClient().post("/tablet/session/refresh", {}, { headers: { Authorization: `Bearer ${staff.token}` } }).catch(() => {});
      }
    };
    arm();
    const evs = ["pointerdown", "keydown", "touchstart", "cc-tablet-activity"];
    evs.forEach((ev) => window.addEventListener(ev, arm));
    const onHide = () => {
      if (expireTabletLock()) return;
      if (document.visibilityState === "hidden" && !isTabletLockHeld()) lock();
    };
    document.addEventListener("visibilitychange", onHide);
    return () => { clearTimeout(lockTimer.current); evs.forEach((ev) => window.removeEventListener(ev, arm)); document.removeEventListener("visibilitychange", onHide); };
  }, [staff, lock]);
  useEffect(() => () => { if (staffRef.current) lock(); }, [lock]); // leaving the route ends the session

  const exitKitchen = () => { lock(); navigate("/login?next=/dashboard"); }; // full sign-in required
  const removeTablet = async () => {
    if (!window.confirm("Remove this tablet? Staff won't be able to use it until a manager registers it again.")) return;
    try {
      await deviceClient().post("/tablet/device/deregister", {}, { headers: { Authorization: `Bearer ${staff.token}` } });
      localStorage.removeItem(DEVICE_KEY); setStaff(null); setRegistered(false); setNotice("This tablet has been removed.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not remove this tablet"); }
  };
  const signedIn = (vr) => { toast.dismiss(); setStaff({ token: vr.actor_token, person: vr.person, is_manager: vr.is_manager, can_manage_pins: vr.can_manage_pins,
    expiresAt: Date.now() + (vr.expires_in || 300) * 1000 }); };

  // ---- point-of-action flow (runs directly under the signed-in person's session) ----
  const startAction = (action) => {
    const s = staffRef.current;
    if (s) { action.run(s.token, { is_manager: s.is_manager, person: s.person, expiresAt: s.expiresAt }); return; }
    setFlow({ stage: "roster", action });
  };
  const pickPerson = (p) => { setSel(p); setFlow((f) => ({ ...f, stage: "pin" })); };
  const submitPin = async (pin) => {
    setBusy(true);
    try {
      const d = deviceClient();
      const vr = await d.post("/tablet/verify-pin", { person_id: sel.person_id, pin });
      const { actor_token, is_manager, expires_in } = vr.data;
      const action = flow.action;
      setFlow(null);
      await action.run(actor_token, { is_manager, person: sel, expiresAt: Date.now() + (expires_in || 300) * 1000 });
    } catch (e) {
      const st = e?.response?.status;
      toast.error(st === 429 ? "Too many attempts — try again shortly" : st === 401 ? "Incorrect PIN" : (e?.response?.data?.detail || "Could not verify"));
    } finally { setBusy(false); }
  };

  const doClock = (kind) => startAction({
    run: async (token, { person }) => {
      const ac = actorClient(token);
      try {
        await ac.post(`/workforce/attendance/${kind === "in" ? "start" : "end"}`, { site_id: status?.device?.site_id });
        toast.success(`${person.display_name} clocked ${kind === "in" ? "in" : "out"}`);
        loadStatus();
      } catch (e) { toast.error(e?.response?.data?.detail || "Could not record"); }
    },
  });

  const openTasks = (f = "all") => { setTaskFilter(f); setView("tasks"); };

  const openManager = () => startAction({
    run: async (token, { is_manager }) => {
      if (!is_manager) { toast.error("Manager PIN required"); return; }
      const ac = actorClient(token);
      try {
        const r = await ac.get(`/tablet/manager/overview?site_id=${status?.device?.site_id || ""}`);
        setSel({ overview: r.data }); setView("manager"); armLock();
      } catch (e) { toast.error(e?.response?.data?.detail || "Could not open manager view"); }
    },
  });

  // auto-lock personal sessions after inactivity
  const [board, setBoard] = useState(null);
  const [numpad, setNumpad] = useState(null);
  const openBoard = async (name) => {
    setView(`board-${name}`);
    try { const r = await deviceClient().get(`/tablet/board/${name}`); setBoard({ name, data: r.data }); }
    catch { setBoard({ name, data: null }); }
  };
  const recordTemp = (unit) => setNumpad({
    label: unit.name || unit.storage_unit || "Equipment",
    onValue: (val) => { setNumpad(null); startAction({ run: async (token) => {
      try { await actorClient(token).post("/temperatures", { record_type: "cold_storage", temperature: Number(val), storage_unit: unit.name || "", venue_id: status?.device?.site_id, entry_method: "kitchen_tablet" }); toast.success("Temperature recorded"); openBoard("temperatures"); }
      catch (e) { toast.error(e?.response?.data?.detail || "Could not record"); } } }); },
  });
  const [delivery, setDelivery] = useState(null);
  const uploadFile = async (token, fileObj) => {
    const fd = new FormData(); fd.append("file", fileObj);
    const r = await axios.post(`${API}/upload`, fd, { headers: { Authorization: `Bearer ${token}` } });
    return r.data.file_id || r.data.id;
  };
  const scanInvoice = (fileObj, applyPrefill, setScannedId, scannedId, setScanning) => startAction({ run: async (token, { expiresAt }) => {
    setScanning(true);
    try {
      const fid = scannedId || await uploadFile(token, fileObj); setScannedId(fid);
      const { data } = await tabletExtract({ client: actorClient(token), fileId: fid, siteId: status?.device?.site_id, sessionExpiresAt: expiresAt });
      applyPrefill(data || {});
      const n = data?.proposal?.line_items?.length || 0;
      const flagged = (data?.proposal?.line_items || []).filter((li) => li?.uncertain_fields?.length || li?.conflicts || li?.review_reasons?.length).length;
      toast.success(data?.status === "unsupported" ? "Can't scan this file — enter details manually"
        : `Scanned — review and confirm${n ? ` (${n} row(s) read${flagged ? `, ${flagged} to check` : ""})` : ""}`);
    } catch (e) {
      toast.error(e?.code === "ai_timeout" ? "Still reading — tap Scan again in a minute to collect the result"
        : (e?.response?.data?.detail || e?.message || "Could not scan — enter details manually"));
    } finally { setScanning(false); } } });
  const commitDelivery = (data, fileObj, scannedId) => startAction({ run: async (token) => {
    try {
      let file_id = scannedId || null;
      if (!file_id && fileObj) file_id = await uploadFile(token, fileObj);
      const body = { operation_id: `kt_${Date.now()}_${Math.random().toString(36).slice(2)}`,
        acceptance: data.acceptance, supplier_id: data.supplier_id, venue_id: status?.device?.site_id,
        reference: { notes: data.notes || "" }, file_id, checks: data.checks,
        corrective_action: data.corrective || "", temperatures: data.temps || [] };
      await actorClient(token).post("/deliveries", body);
      toast.success(`Delivery ${data.acceptance === "rejected" ? "rejected" : "recorded"}`);
      setDelivery(null); openBoard("deliveries");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not record delivery"); } } });

  const armLock = () => {};
  const lockNow = () => { setView("home"); setSel(null); };

  // -------------------------------------------------------- render
  if (!registered) {
    return <TabletSetup notice={notice} onRegistered={() => { setNotice(null); setRegistered(true); }} />;
  }
  if (!staff) {
    return <StaffPinScreen status={status} roster={roster} deviceClient={deviceClient} onSignedIn={signedIn} onExit={exitKitchen} reload={loadStatus} />;
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="kitchen-tablet-home">
      {/* header */}
      <div className="sticky top-0 z-[60] bg-slate-950 flex flex-wrap items-center justify-between gap-3 px-4 sm:px-6 py-4 border-b border-slate-800">
        <div>
          <p className="text-xs uppercase tracking-widest text-brand font-semibold">{status?.device?.site_name || "Kitchen"} · Kitchen Tablet</p>
          <h1 className="text-xl font-bold" data-testid="tablet-staff-name">{staff.person?.display_name}</h1>
          <p className="text-xs text-slate-400" data-testid="tablet-staff-role">{staff.person?.job_title || (staff.is_manager ? "Manager" : "Staff")} · locks after 60 s without activity</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {staff.can_manage_pins && <button onClick={removeTablet} data-testid="tablet-remove-device" className="px-3 py-2 rounded-xl bg-slate-800 text-red-300 text-sm mr-6">Remove tablet</button>}
          {staff.can_manage_pins && <button onClick={() => setView("pins")} data-testid="tablet-manage-pins" className="px-3 py-2 rounded-xl bg-slate-800 text-sm">Staff PINs</button>}
          <button onClick={exitKitchen} data-testid="tablet-exit" className="px-3 py-2 rounded-xl bg-slate-800 text-sm flex items-center gap-2"><LogOut size={16} /> Exit to CaterCTRL</button>
          <button onClick={() => lock("Finished. Next person, tap your name.")} data-testid="tablet-finish" className="px-5 py-3 rounded-xl bg-brand text-white font-semibold">Finish</button>
        </div>
      </div>

      {view === "home" && (
        <div className="p-6">
          <div className={`rounded-2xl p-5 mb-6 flex items-center gap-4 ${status?.needs_attention_count ? "bg-red-500/15 border border-red-500/40" : "bg-emerald-500/10 border border-emerald-500/30"}`} data-testid="needs-attention">
            {status?.needs_attention_count
              ? <><AlertTriangle className="text-red-300" /><div><p className="font-bold text-lg">{status.needs_attention_count} item{status.needs_attention_count > 1 ? "s" : ""} need attention</p><p className="text-sm text-red-200/80">Overdue across your kitchen</p></div></>
              : <><ShieldCheck className="text-emerald-300" /><div><p className="font-bold text-lg">Kitchen on track</p><p className="text-sm text-emerald-200/80">Nothing overdue right now</p></div></>}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {Object.entries(status?.tiles || {}).map(([key, v]) => {
              const m = TILE_META[key]; if (!m) return null; const Icon = m.icon;
              const overdue = v.overdue || 0; const due = v.due || 0;
              return (
                <div key={key} data-testid={`tile-${key}`} className="rounded-2xl bg-slate-900 border border-slate-800 p-5">
                  <Icon className={m.color} size={30} />
                  <p className="mt-3 font-semibold">{m.label}</p>
                  {key === "temperatures"
                    ? <p className="text-sm text-slate-400 mt-1">{v.open_rounds || 0} open round{(v.open_rounds || 0) === 1 ? "" : "s"}</p>
                    : <p className="text-sm mt-1"><span className={overdue ? "text-red-300 font-bold" : "text-slate-400"}>{overdue} overdue</span> · <span className="text-slate-400" data-testid={`tile-${key}-open`}>{v.open ?? due} open</span></p>}
                </div>
              );
            })}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <button onClick={() => doClock("in")} data-testid="action-clock-in" className="rounded-2xl bg-brand/20 border border-brand/40 p-6 text-left active:bg-brand/30"><Clock className="text-brand" size={28} /><p className="mt-3 font-semibold">Clock In</p></button>
            <button onClick={() => doClock("out")} data-testid="action-clock-out" className="rounded-2xl bg-slate-900 border border-slate-800 p-6 text-left active:bg-slate-800"><Clock className="text-slate-300" size={28} /><p className="mt-3 font-semibold">Clock Out</p></button>
            <button onClick={() => openTasks("all")} data-testid="action-my-tasks" className="rounded-2xl bg-slate-900 border border-slate-800 p-6 text-left active:bg-slate-800"><ListChecks className="text-amber-300" size={28} /><p className="mt-3 font-semibold">Kitchen tasks</p><p className="text-xs text-slate-400">Everything open in this kitchen</p></button>
            {staff.is_manager && <button onClick={openManager} data-testid="action-manager" className="rounded-2xl bg-slate-900 border border-slate-800 p-6 text-left active:bg-slate-800"><ShieldCheck className="text-violet-300" size={28} /><p className="mt-3 font-semibold">Manager View</p></button>}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            <button onClick={() => openBoard("temperatures")} data-testid="board-temperatures-tile" className="rounded-2xl bg-slate-900 border border-slate-800 p-6 text-left active:bg-slate-800"><Thermometer className="text-sky-300" size={28}/><p className="mt-3 font-semibold">Temperatures</p></button>
            <button onClick={() => openTasks("cleaning")} data-testid="board-cleaning-tile" className="rounded-2xl bg-slate-900 border border-slate-800 p-6 text-left active:bg-slate-800"><SprayCan className="text-emerald-300" size={28}/><p className="mt-3 font-semibold">Cleaning</p></button>
            <button onClick={() => openTasks("prep")} data-testid="board-prep-tile" className="rounded-2xl bg-slate-900 border border-slate-800 p-6 text-left active:bg-slate-800"><ChefHat className="text-amber-300" size={28}/><p className="mt-3 font-semibold">Prep</p></button>
            {status?.tiles?.checklists && <button onClick={() => openTasks("checklists")} data-testid="board-checklists-tile" className="rounded-2xl bg-slate-900 border border-slate-800 p-6 text-left active:bg-slate-800"><ClipboardCheck className="text-violet-300" size={28}/><p className="mt-3 font-semibold">Checklists</p></button>}
            <button onClick={() => openBoard("deliveries")} data-testid="board-deliveries-tile" className="rounded-2xl bg-slate-900 border border-slate-800 p-6 text-left active:bg-slate-800"><ClipboardCheck className="text-violet-300" size={28}/><p className="mt-3 font-semibold">Deliveries</p></button>
          </div>
        </div>
      )}

      {view === "tasks" && staffClient && (
        <TabletTasks key={staff.token} client={staffClient} siteId={status?.device?.site_id} initialFilter={taskFilter} onBack={() => setView("home")} />
      )}

      {view === "manager" && (
        <div className="p-6" data-testid="tablet-manager-view">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-xl font-bold">Manager overview</h2>
            <button onClick={lockNow} className="px-4 py-2 rounded-xl bg-slate-800 text-sm">Close</button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {[["Open cleaning", sel?.overview?.open_cleaning], ["Open prep", sel?.overview?.open_prep], ["Open checklists", sel?.overview?.open_checklists], ["Open temp rounds", sel?.overview?.open_temperature_rounds]].map(([l, n]) => (
              <div key={l} className="rounded-2xl bg-slate-900 border border-slate-800 p-5"><p className="text-3xl font-bold">{n ?? 0}</p><p className="text-sm text-slate-400 mt-1">{l}</p></div>
            ))}
          </div>
          <h3 className="font-semibold mb-2">Clocked in now</h3>
          <div className="space-y-2">
            {(sel?.overview?.staff_clocked_in || []).length === 0 && <p className="text-slate-400 text-sm">No one clocked in.</p>}
            {(sel?.overview?.staff_clocked_in || []).map((s, i) => (
              <div key={i} className="rounded-xl bg-slate-900 border border-slate-800 p-3 flex justify-between"><span>{s.person_name}</span><span className="text-xs text-slate-500">{s.site_name}</span></div>
            ))}
          </div>
        </div>
      )}

      {view?.startsWith("board-") && (
        <div className="p-6" data-testid={`tablet-${view}`}>
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-xl font-bold capitalize">{board?.name}</h2>
            <button onClick={() => { setView("home"); setBoard(null); }} data-testid="board-back" className="px-4 py-2 rounded-xl bg-slate-800 text-sm flex items-center gap-2"><ArrowLeft size={14}/> Home</button>
          </div>
          {board?.name === "temperatures" && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {(board.data?.units || []).map((u, i) => (
                <button key={i} onClick={() => recordTemp(u)} data-testid={`temp-unit-${i}`} className="rounded-2xl bg-slate-900 border border-slate-800 p-5 text-left active:bg-brand/20">
                  <Thermometer className="text-sky-300" size={24}/><p className="mt-2 font-semibold">{u.name || u.storage_unit || "Equipment"}</p><p className="text-xs text-slate-400">Tap to record</p></button>
              ))}
              {(board.data?.units || []).length === 0 && <p className="text-slate-400">No equipment configured for this site.</p>}
            </div>
          )}
          {board?.name === "deliveries" && (
            <div>
              <button onClick={() => setDelivery({})} data-testid="delivery-new" className="mb-5 px-5 py-3 rounded-2xl bg-brand text-white font-semibold">+ Record a delivery</button>
              <p className="text-xs uppercase tracking-wider text-slate-500 mb-2">Recent</p>
              {(board.data?.recent || []).map((d, i) => (
                <div key={i} className="rounded-xl bg-slate-900 border border-slate-800 p-3 mb-2 flex justify-between"><span>{d.supplier_name || "Delivery"}{d.has_temperature_exception ? <span className="ml-2 text-xs text-red-300">temp exception</span> : null}</span><span className="text-xs text-slate-500">{d.acceptance || ""}</span></div>
              ))}
              {(board.data?.recent || []).length === 0 && <p className="text-slate-600 text-sm">No deliveries yet today.</p>}
            </div>
          )}
        </div>
      )}
      {numpad && (
        <div className="fixed inset-0 z-50 bg-slate-950/90 flex items-center justify-center p-6" data-testid="tablet-numpad">
          <div className="w-full max-w-sm bg-slate-900 rounded-3xl p-7 border border-slate-700">
            <div className="flex items-center justify-between mb-4"><p className="text-slate-300">{numpad.label} °C</p><button onClick={() => setNumpad(null)}><X className="text-slate-500" size={22}/></button></div>
            <NumPad onValue={numpad.onValue} />
          </div>
        </div>
      )}
      {delivery && (
        <DeliveryCapture suppliers={board?.data?.suppliers || []} onCancel={() => setDelivery(null)} onCommit={commitDelivery} onScan={scanInvoice} />
      )}
      {view === "pins" && <div className="fixed inset-0 z-40 bg-slate-950 overflow-auto"><ManagePins client={actorClient(staff.token)} onBack={() => { setView("home"); loadStatus(); }} /></div>}

      {flow?.stage === "roster" && <RosterPicker roster={roster} onPick={pickPerson} onCancel={() => setFlow(null)} />}
      {flow?.stage === "pin" && <PinPad person={sel} onSubmit={submitPin} onCancel={() => { setFlow(null); setSel(null); }} busy={busy} />}
    </div>
  );
}

